import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContributionAccountField } from '@/components/giving/contribution-account-field';
import { ContributionGatheringFields } from '@/components/giving/contribution-gathering-fields';
import {
  ContributionGiverFields,
  contributionGiverPayload,
  contributionGiverReady,
  type ContributionGiverKind,
} from '@/components/giving/contribution-giver-fields';
import { RecordContributionSaveOptions } from '@/components/giving/record-contribution-save-options';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { resolveOfferingOccurrence } from '@/lib/resolve-offering-occurrence';
import {
  loadRecordContributionPreferences,
  persistRecordContributionPreferences,
} from '@/lib/record-contribution-preferences';
import { useChurchStore } from '@/stores/church-store';
import {
  accountKindForMethod,
  CONTRIBUTION_METHODS,
  type ContributionMethod,
} from '@/lib/contribution-methods';

const todayIso = () => new Date().toISOString().slice(0, 10);

export interface RecordContributionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fundId: string;
  fundName: string;
  currency: string;
  /** Dialog title; defaults to the contribution record label. */
  title?: string;
}

export const RecordContributionDialog = ({
  open,
  onOpenChange,
  fundId,
  fundName,
  currency,
  title,
}: RecordContributionDialogProps) => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const resolvedCurrency = currency || church?.baseCurrency || 'GHS';

  const [giverKind, setGiverKind] = useState<ContributionGiverKind>('internal');
  const [memberId, setMemberId] = useState<string | null>(null);
  const [externalPartyId, setExternalPartyId] = useState<string | null>(null);
  const [giverName, setGiverName] = useState('');
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [contributionDate, setContributionDate] = useState<string | null>(null);
  const [programId, setProgramId] = useState<string | null>(null);
  const [serviceDate, setServiceDate] = useState<string | null>(null);
  const [method, setMethod] = useState<ContributionMethod>('cash');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [savePreferences, setSavePreferences] = useState(() =>
    loadRecordContributionPreferences(church?.id),
  );
  const [formKey, setFormKey] = useState(0);

  const resetForm = () => {
    setGiverKind('internal');
    setMemberId(null);
    setExternalPartyId(null);
    setGiverName('');
    setAmountMinor(null);
    setContributionDate(todayIso());
    setProgramId(null);
    setServiceDate(null);
    setMethod('cash');
    setAccountId(null);
    setReference('');
    setNote('');
  };

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }
    setContributionDate(todayIso());
  }, [open]);

  const giver = { kind: giverKind, memberId, externalPartyId, giverName };
  const giverReady = contributionGiverReady(giver);

  const record = useMutation({
    mutationFn: async () => {
      const occurrenceId =
        programId && serviceDate ? await resolveOfferingOccurrence(programId, serviceDate) : null;
      return api.post('/giving/contributions', {
        fundId,
        ...contributionGiverPayload(giver),
        amountMinor,
        currency: resolvedCurrency,
        contributionDate,
        occurrenceId,
        method,
        financialPositionItemId: accountKindForMethod(method) ? accountId : null,
        reference: reference || null,
        note: note || null,
      });
    },
    onSuccess: () => {
      toast.success(t('saved'));
      void queryClient.invalidateQueries({ queryKey: ['giving', 'contributions'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'insights'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'summary'] });
      void queryClient.invalidateQueries({ queryKey: ['occurrences'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds', fundId] });
      if (savePreferences.addAnother) {
        resetForm();
        setFormKey((current) => current + 1);
        return;
      }
      onOpenChange(false);
      resetForm();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) resetForm();
      }}
    >
      <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
        <form
          key={formKey}
          className="min-h-0"
          onSubmit={(event) => {
            event.preventDefault();
            if (amountMinor == null || !contributionDate || !giverReady) return;
            if (programId && !serviceDate) return;
            record.mutate();
          }}
        >
          <DialogBody
            className="max-h-[calc(92dvh-3rem)]"
            contentClassName="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>{title ?? t('contributions.record')}</DialogTitle>
              <DialogDescription>{fundName}</DialogDescription>
            </DialogHeader>
            <ContributionGiverFields
              open={open}
              kind={giverKind}
              onKindChange={(next) => {
                setGiverKind(next);
                setMemberId(null);
                setExternalPartyId(null);
                setGiverName('');
              }}
              memberId={memberId}
              onMemberIdChange={setMemberId}
              externalPartyId={externalPartyId}
              onExternalPartyChange={setExternalPartyId}
              giverName={giverName}
              onGiverNameChange={setGiverName}
            />
            <div className="flex flex-col gap-1.5">
              <Label>{t('form.amount')}</Label>
              <MoneyInput
                value={amountMinor}
                onChange={setAmountMinor}
                currency={resolvedCurrency}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('form.date')}</Label>
              <DatePicker
                value={contributionDate}
                onChange={setContributionDate}
                popoverPortal="dialog"
              />
            </div>
            <ContributionGatheringFields
              open={open}
              programId={programId}
              serviceDate={serviceDate}
              onProgramChange={setProgramId}
              onServiceDateChange={(next) => {
                setServiceDate(next);
                if (next) setContributionDate(next);
              }}
            />
            <div className="flex flex-col gap-1.5">
              <Label>{t('form.method')}</Label>
              <Listbox
                value={method}
                onChange={(value) => {
                  const next = (value as typeof method) ?? 'cash';
                  setMethod(next);
                  setAccountId(null);
                }}
                options={CONTRIBUTION_METHODS.map((value) => ({
                  value,
                  label: t(`methods.${value}`),
                }))}
                popoverPortal="dialog"
              />
            </div>
            <ContributionAccountField
              open={open}
              method={method}
              currency={resolvedCurrency}
              value={accountId}
              onChange={setAccountId}
            />
            <div className="flex flex-col gap-1.5">
              <Label>{t('form.reference')}</Label>
              <Input value={reference} onChange={(event) => setReference(event.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('form.note')}</Label>
              <Textarea value={note} onChange={(event) => setNote(event.target.value)} rows={2} />
            </div>
            <RecordContributionSaveOptions
              addAnother={savePreferences.addAnother}
              onAddAnotherChange={(addAnother) => {
                const next = { ...savePreferences, addAnother };
                setSavePreferences(next);
                persistRecordContributionPreferences(church?.id, next);
              }}
              disabled={record.isPending}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                loading={record.isPending}
                disabled={
                  amountMinor == null ||
                  !contributionDate ||
                  !giverReady ||
                  Boolean(programId && !serviceDate) ||
                  Boolean(accountKindForMethod(method) && !accountId)
                }
              >
                {t('form.save')}
              </Button>
            </DialogFooter>
          </DialogBody>
        </form>
      </DialogContent>
    </Dialog>
  );
};
